import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { CountriesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as CountryActions from "../../../actions/countries";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { LookupsLayout } from "../../index";

import "./CountriesPage.scss";

interface CountriesPageProps {
    countries: Dtos.Country[];
    formFilter: IFormFilter;
    loadingCountries: boolean;
    loadCountriesSuccess: boolean;
    loadCountriesFailure: boolean;
    loadCountriesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearCountries: CountryActions.ICountryClearActionCreator;
    loadCountries: CountryActions.ICountryLoadActionCreator;
    filter: CountryActions.ICountryFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const CountriesPage: FC<CountriesPageProps> = ({
    countries,
    formFilter,
    loadingCountries,
    loadCountriesSuccess,
    loadCountriesFailure,
    loadCountriesErrorCode,
    permissions,
    pageSize,
    clearCountries,
    loadCountries,
    filter,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showCountries = !loadingCountries && loadCountriesSuccess;


    // Load countries
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(CountriesBreadcrumbs());

            loadCountries();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        CountriesBreadcrumbs,
        loadCountries,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadCountriesFailure) {
            navigate(`/error/${loadCountriesErrorCode}`);
        }
    }, [loadCountriesFailure, loadCountriesErrorCode, navigate]);

    // Cleanup effect to clear the countries on component unmount
    useEffect(() => {
        return () => {
            clearCountries();
        };
    }, [clearCountries]);

    const createTable = useMemo(() => {
        if (!countries || countries.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no countries to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = countries.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={countries}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : countries.length
                    }
                    pageSize={showPagination ? pageSize : countries.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no countries to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string = `/settings/general/countries/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [countries, pageSize, PAGE_SIZE_OPTIONS, setPageSize, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Countries</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.CountriesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/general/countries/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showCountries ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showCountries ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showCountries, createTable]);

    return (
        <LookupsLayout
            loading={loadingCountries}
            permission={Dtos.Permission.CountriesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<CountriesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        countries: state.countries.formFilteredData!,
        formFilter: state.countries.formFilter,
        loadingCountries:
            state.countries.loadState &&
            state.countries.loadState.status == RequestState.Pending,
        loadCountriesSuccess:
            state.countries.loadState &&
            state.countries.loadState.status == RequestState.Success,
        loadCountriesFailure:
            state.countries.loadState &&
            state.countries.loadState.status == RequestState.Failure,
        loadCountriesErrorCode: state.countries.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<CountriesPageProps> => {
    return {
        loadCountries: bindActionCreators(CountryActions.LoadCountry, dispatch),
        clearCountries: bindActionCreators(CountryActions.Clear, dispatch),
        filter: bindActionCreators(CountryActions.Filter, dispatch),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CountriesPage);
